/**
 * Token Refresh Middleware and Manager
 *
 * Handles automatic token refresh logic with request queuing and 401 error handling.
 * Prevents duplicate refresh calls and manages concurrent requests.
 *
 * Flow:
 * 1. API call receives 401 Unauthorized
 * 2. Middleware attempts token refresh via POST /api/auth/refresh (credentials: include)
 * 3. Backend reads the refresh_token cookie and sets new HTTP-only cookies on success
 * 4. If refresh succeeds, retry original request (browser auto-sends new cookies)
 * 5. If refresh fails, return 401 error
 * 6. Handle 2FA if required after token refresh
 *
 * Note: Uses direct fetch to avoid circular dependency with apiClient
 */

import { useAuthStore } from '../../stores/authStore';
import { localStorageUtils } from '../../utils/localStorage';
import { API_BASE_URL, ApiResponse, ApiResult } from '../apiTypes';
import { processTwoFAResponse } from './twoFactorMiddleware';

// ============================================================================
// Token Refresh Manager
// ============================================================================

/** Singleton promise -- prevents duplicate refresh calls from concurrent 401s */
let refreshPromise: Promise<boolean> | null = null;

/**
 * Refreshes the authentication token via HTTP-only cookies.
 *
 * Uses a singleton pattern to prevent duplicate refresh calls when multiple
 * requests fail simultaneously. If a refresh is already in progress, returns
 * the existing promise so all requests wait for the same refresh.
 *
 * The backend reads the refresh_token from the cookie and sets new
 * access_token / refresh_token cookies on the response.
 *
 * @returns Promise resolving to true if refresh succeeded, false otherwise
 */
export async function refreshAuthToken(): Promise<boolean> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const url = `${API_BASE_URL}/api/auth/refresh`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Refresh failed: ${response.status}`);
      }

      const backendResponse = (await response.json()) as ApiResponse<unknown>;

      if (!backendResponse.success) {
        throw new Error('Invalid refresh response');
      }

      return true;
    } catch {
      localStorageUtils.clearSessionAuthenticated();
      useAuthStore.getState().clearUser();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ============================================================================
// Token Refresh Middleware
// ============================================================================

type RetryRequestFn<T> = (twoFactorCode?: string) => Promise<ApiResult<T>>;

/**
 * Process 401 Unauthorized response by attempting token refresh and retrying.
 *
 * @returns API result if token refresh was handled, null if no refresh was needed
 */
export async function processTokenRefresh<T>(
  response: Response,
  backendResponse: ApiResponse<T>,
  skipRefreshOnUnauthorized: boolean,
  url: string,
  requestOptions: RequestInit,
  retryRequest: RetryRequestFn<T>
): Promise<ApiResult<T> | null> {
  if (response.status !== 401 || skipRefreshOnUnauthorized) {
    return null;
  }

  const refreshed = await refreshAuthToken();

  if (!refreshed) {
    const errorMessage =
      backendResponse.message || backendResponse.error || `HTTP ${response.status}: ${response.statusText}`;
    return {
      data: null,
      error: {
        message: errorMessage,
        status: response.status,
        details: backendResponse.details,
      },
    };
  }

  // Retry the original request -- the browser will send the new cookies automatically
  const retryResponse = await fetch(url, requestOptions);

  if (!retryResponse.ok) {
    const retryBackendResponse = (await retryResponse.json()) as ApiResponse<T>;
    return {
      data: null,
      error: {
        message:
          retryBackendResponse.error ||
          retryBackendResponse.message ||
          `HTTP ${retryResponse.status}: ${retryResponse.statusText}`,
        status: retryResponse.status,
        details: retryBackendResponse.details,
      },
    };
  }

  const retryBackendResponse = (await retryResponse.json()) as ApiResponse<T>;

  const makeRequestWithCode = (code: string) => retryRequest(code);
  const retryTwoFAResult = await processTwoFAResponse(retryBackendResponse, undefined, makeRequestWithCode);
  if (retryTwoFAResult !== null) {
    return retryTwoFAResult;
  }

  if (!retryBackendResponse.success) {
    return {
      data: null,
      error: {
        message: retryBackendResponse.error || retryBackendResponse.message || '',
        status: retryResponse.status,
        details: retryBackendResponse.details,
      },
    };
  }

  return { data: retryBackendResponse.data ?? null, error: null };
}
